import { useCallback, useId, useMemo, useState } from "react";
import type React from "react";

import { buildAriaDescribedBy, buildTestIdPrefix } from "src/utils/fieldIds";
import { useMultiSelectOptions } from "./useMultiSelectOptions";
import { CREATE_PREFIX, isCreateValue } from "./useCreatableItems";
import { SIZE_CONFIG } from "src/components/Select/constants";
import type { SelectProps } from "src/components/Select/types";

type SingleSelectProps = Extract<SelectProps, { isMulti?: false }>;

const defaultFormatCreateLabel = (v: string) => `Create "${v}"`;

export function useSelectState(props: SingleSelectProps) {
  const {
    id: idProp,
    size = "medium",
    label = "",
    error = "",
    helpText = "",
    value: valueProp,
    defaultValue,
    onChange,
    dataTestId,
    isCreatable = false,
    onCreateOption,
    formatCreateLabel = defaultFormatCreateLabel,
    formatOptionLabel,
    formatTriggerLabel,
    isClearable = false,
    isDisabled = false,
    required = false,
    isSearchable = true,
    autoHighlight = true,
    onOpenChange,
    children,
  } = props;

  const opt = useMultiSelectOptions(props);

  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = `error_${id}`;
  const helpTextId = `helpText_${id}`;
  const sizeConfig = SIZE_CONFIG[size];

  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue ?? ""
  );
  const currentValue = isControlled ? (valueProp ?? "") : internalValue;

  const applyValue = useCallback(
    (next: string) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const handleValueChange = useCallback(
    (next: string | null) => {
      const raw = next ?? "";

      // Handle the creatable "Create X" option
      if (raw.startsWith(CREATE_PREFIX)) {
        const newValue = raw.slice(CREATE_PREFIX.length);
        onCreateOption?.(newValue);
        applyValue(newValue);
        return;
      }

      applyValue(raw);
    },
    [applyValue, onCreateOption]
  );

  const [inputValue, setInputValueState] = useState("");
  const trimmedInput = inputValue.trim();

  const combinedHandleInputValueChange = useCallback(
    (value: string) => {
      setInputValueState(value);
      if (opt.isAsync) opt.handleAsyncInputChange(value);
    },
    [opt.isAsync, opt.handleAsyncInputChange]
  );

  const handleClear = useCallback(() => {
    applyValue("");
  }, [applyValue]);

  const showCreateOption = useMemo(() => {
    if (
      !isCreatable ||
      trimmedInput.length === 0 ||
      currentValue === trimmedInput
    )
      return false;

    const lower = trimmedInput.toLowerCase();
    return !opt.flatOptions.some(
      o => o.label.toLowerCase() === lower || o.value.toLowerCase() === lower
    );
  }, [isCreatable, trimmedInput, currentValue, opt.flatOptions]);

  const effectiveItems = useMemo(() => {
    if (children) return undefined;
    const base = opt.baseUiItems;
    if (!showCreateOption) return base;
    const createValue = `${CREATE_PREFIX}${trimmedInput}`;
    if (!opt.isGrouped) {
      return [...((base as string[]) ?? []), createValue];
    }
    return [
      ...((base as { label: string; items: string[] }[]) ?? []),
      { label: "", items: [createValue] },
    ];
  }, [
    children,
    opt.baseUiItems,
    opt.isGrouped,
    showCreateOption,
    trimmedInput,
  ]);

  const creatableFilter = useCallback(
    (
      itemValue: string,
      query: string,
      itemToString?: (v: string) => string
    ) => {
      if (isCreateValue(itemValue)) return true;
      const label = itemToString?.(itemValue) ?? itemValue;
      return label.toLowerCase().includes(query.toLowerCase());
    },
    []
  );

  const ariaDescribedBy = buildAriaDescribedBy(
    error,
    errorId,
    helpText,
    helpTextId
  );
  const testIdPrefix = buildTestIdPrefix(dataTestId, label);
  const showClearButton = isClearable && !!currentValue && !isDisabled;

  const flatOptionsMap = useMemo(
    () => new Map(opt.flatOptions.map(o => [o.value, o])),
    [opt.flatOptions]
  );

  const labelForValue = useCallback(
    (val: string) => {
      if (isCreateValue(val)) {
        return formatCreateLabel(val.slice(CREATE_PREFIX.length));
      }
      return flatOptionsMap.get(val)?.label ?? val ?? "";
    },
    [flatOptionsMap, formatCreateLabel]
  );

  const renderOption = useCallback<(val: string) => React.ReactNode>(
    val => {
      if (formatOptionLabel && !isCreateValue(val)) {
        const option = flatOptionsMap.get(val);
        if (option) return formatOptionLabel(option);
      }
      return labelForValue(val);
    },
    [formatOptionLabel, flatOptionsMap, labelForValue]
  );

  const renderTrigger = useCallback<(val: string) => React.ReactNode>(
    val => {
      if (formatTriggerLabel && !isCreateValue(val)) {
        const option = flatOptionsMap.get(val);
        if (option) return formatTriggerLabel(option);
      }
      return renderOption(val);
    },
    [formatTriggerLabel, flatOptionsMap, renderOption]
  );

  return {
    ...opt,
    id,
    errorId,
    helpTextId,
    sizeConfig,
    isControlled,
    currentValue,
    effectiveItems,
    creatableFilter,
    handleValueChange,
    handleInputValueChange: combinedHandleInputValueChange,
    handleClear,
    showCreateOption,
    showClearButton,
    ariaDescribedBy,
    testIdPrefix,
    labelForValue,
    renderOption,
    renderTrigger,
    flatOptionsMap,
    label,
    error,
    helpText,
    isDisabled,
    required,
    isSearchable,
    autoHighlight,
    onOpenChange,
    isClearable,
  };
}
